import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { login as serviceLogin } from '../../../lib/store/common-actions';
import { NullableString } from '../../../lib/util/types';

import IconCloseWhite from '../../../assets/img/icon-close-white.svg';
import { ClickableImage, ClickableSpan } from '../../../components';
import LogoAndCloseButton from '../../../components/LogoAndCloseButton/LogoAndCloseButton';
import { useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';

import styles from './Layout.module.scss';

type OwnProps = {
  displayNextButton: boolean;
  displayLoginButton: boolean;
  displayBackButton: boolean;
  displayFooter: boolean;
  disabledNextButton?: boolean;
  showCloseButtonOnTop?: boolean;
  formSubtitle?: string;
  contentStyle?: string;
  nextButtonText: NullableString;
  formTitle: string;
  onBackBtnPress?: () => void;
  onSubmit: () => void;
  onClose?: () => void;
  handleOnTopCloseButton?: () => void;
  children: JSX.Element | JSX.Element[];
  transparentContent?: boolean;
  additionalHeight?: number;
};

type Props = OwnProps & ReduxProps;

const BASE_WEB_HEIGHT = 561;
const BASE_MOB_HEIGHT = 482;

const Layout = (props: Props): JSX.Element => {
  const {
    formTitle,
    children,
    onClose,
    onSubmit,
    formSubtitle,
    onBackBtnPress,
    login,
    nextButtonText,
    disabledNextButton,
    displayFooter,
    displayBackButton,
    displayLoginButton,
    displayNextButton,
    showCloseButtonOnTop,
    handleOnTopCloseButton,
    contentStyle,
    transparentContent,
    additionalHeight,
  } = props;
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const baseHeight = isMobile ? BASE_MOB_HEIGHT : BASE_WEB_HEIGHT;
  const adjustedHeight = baseHeight + (additionalHeight || 0);
  const heightClass = adjustedHeight ? styles.overrideHeight : '';

  return (
    <div className={styles.container}>
      {showCloseButtonOnTop && (
        <div className={styles.closeButtonWrapper}>
          <ClickableImage
            alt=""
            src={IconCloseWhite}
            className={styles.closeIcon}
            onClick={() => (handleOnTopCloseButton ? handleOnTopCloseButton() : navigate(-1))}
            style={{ cursor: 'pointer' }}
          />
        </div>
      )}
      <div className={classNames(styles.header, { [styles.contentMob]: isMobile })}>
        <LogoAndCloseButton
          displayCloseButton
          displayBackButton={displayBackButton}
          onBackBtnPress={onBackBtnPress}
          onClose={onClose}
        />
      </div>
      <div className={styles.wrapperContent}>
        <div
          className={classNames(
            styles.content,
            { [styles.transparentContent]: transparentContent },
            { [styles.contentMob]: isMobile },
            heightClass,
          )}
        >
          <span className={isMobile ? styles.formTitleMobile : styles.formTitle}>{formTitle}</span>
          {formSubtitle && <span className={styles.formSubtitle}>{formSubtitle}</span>}
          {children}
          {!isMobile && displayNextButton && (
            <div className={styles.row}>
              <Button
                fullWidth
                id="createAccount"
                ariaLabel="create account"
                onClick={onSubmit}
                className={styles.button}
                disabled={disabledNextButton}
              >
                {nextButtonText ?? lang.commonContinueButton()}
              </Button>
            </div>
          )}
          {!isMobile && displayLoginButton && (
            <div className={classNames(styles.row, { [styles.center]: true })}>
              <span className={styles.title}>
                {lang.mobileOnboardingAlreadyHaveAccount()}
                {' '}
                <ClickableSpan onClick={login} className={styles.logInButtonTitle}>
                  {lang.mobileOnboardingLogIn()}
                </ClickableSpan>
              </span>
            </div>
          )}
        </div>
        {isMobile && displayNextButton && (
          <div className={classNames(styles.footer, { [styles.footerMob]: isMobile })}>
            <button
              type="button"
              onClick={onSubmit}
              className={classNames(styles.continueButton)}
              disabled={disabledNextButton}
            >
              {nextButtonText ?? lang.commonContinueButton()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  login: () => {
    dispatch(serviceLogin({ authType: 'login' }));
  },
});

const connector = connect(null, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Layout);
